// src/pages/RegisterInterest.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { interestAPI } from '../services/api'; // +++ Import API +++
import { trackConversion, trackGa4Event } from '../utils/consent';

const BRAND = {
    teal: '#75B9BE',
    cream: '#FDF6EC',
    charcoal: '#2D4042',
    coral: '#F28482',
    white: '#FFFFFF',
    textGrey: '#5D7375'
};

const RegisterInterest = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        school: '',
        role: '',
        heardAboutUs: '',
        website: ''
    });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            // +++ REAL API CALL +++
            await interestAPI.register(formData);
            setSubmitted(true);
            // Conversion signals (consent-gated — no-op without opt-in).
            trackConversion('lead_submitted', { form: 'register_interest' }); // Meta (via GTM)
            trackGa4Event('generate_lead', { form: 'register_interest' });     // GA4
        } catch (error) {
            console.error("Failed to register interest:", error);
            alert("Sorry, something went wrong. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ 
            fontFamily: "'Open Sans', sans-serif", 
            color: BRAND.charcoal, 
            backgroundColor: BRAND.cream, 
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            overflow: 'hidden'
        }}>
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Open+Sans:wght@500;600;700&display=swap');
                    
                    .pattern-bg {
                        background-image: radial-gradient(${BRAND.teal} 15%, transparent 16%), radial-gradient(${BRAND.coral} 15%, transparent 16%);
                        background-size: 60px 60px;
                        background-position: 0 0, 30px 30px;
                        opacity: 0.15;
                        position: absolute;
                        top: 0; left: 0; right: 0; bottom: 0;
                        z-index: 0;
                    }

                    .interest-card {
                        background: ${BRAND.white};
                        border: 3px solid ${BRAND.charcoal};
                        border-radius: 20px;
                        padding: 40px;
                        box-shadow: 8px 8px 0px ${BRAND.teal};
                        width: 100%;
                        max-width: 500px;
                        /* Updated margin to bring box closer to top */
                        margin: 10px auto 40px auto; 
                        position: relative;
                        z-index: 10;
                        /* IMPORTANT: No global box-sizing: border-box here to preserve desktop width */
                    }

                    .form-label {
                        display: block;
                        margin-bottom: 8px;
                        font-family: 'Nunito', sans-serif;
                        font-weight: 800;
                        color: ${BRAND.textGrey};
                        font-size: 0.9rem;
                        text-transform: uppercase;
                    }

                    .form-input {
                        width: 100%;
                        padding: 12px 16px;
                        border-radius: 12px;
                        border: 2px solid ${BRAND.charcoal};
                        background-color: #F9FAFB;
                        font-family: 'Open Sans', sans-serif;
                        font-size: 1rem;
                        color: ${BRAND.charcoal};
                        box-sizing: border-box;
                        transition: all 0.2s ease;
                        margin-bottom: 20px;
                        font-weight: 600;
                    }
                    .form-input:focus {
                        outline: none;
                        border-color: ${BRAND.teal};
                        box-shadow: 0 0 0 4px rgba(117, 185, 190, 0.2);
                        background: white;
                    }

                    .btn-submit {
                        width: 100%;
                        padding: 14px;
                        border-radius: 14px;
                        border: 3px solid ${BRAND.charcoal};
                        background-color: ${BRAND.coral};
                        color: white;
                        font-family: 'Nunito', sans-serif;
                        font-weight: 800;
                        font-size: 1.1rem;
                        cursor: pointer;
                        box-shadow: 4px 4px 0px ${BRAND.charcoal};
                        transition: all 0.1s ease;
                        text-transform: uppercase;
                    }
                    .btn-submit:hover:not(:disabled) {
                        transform: translate(2px, 2px);
                        box-shadow: 2px 2px 0px ${BRAND.charcoal};
                    }
                    .btn-submit:active:not(:disabled) {
                        transform: translate(4px, 4px);
                        box-shadow: 0 0 0 0 ${BRAND.charcoal};
                    }
                    .btn-submit:disabled {
                        opacity: 0.7;
                        cursor: not-allowed;
                    }

                    .btn-back {
                        display: inline-block;
                        margin-bottom: 20px;
                        color: ${BRAND.textGrey};
                        text-decoration: none;
                        font-weight: 700;
                        font-family: 'Nunito', sans-serif;
                        font-size: 0.9rem;
                    }
                    .btn-back:hover { text-decoration: underline; color: ${BRAND.charcoal}; }

                    .hp-field {
                        position: absolute;
                        left: -9999px;
                        top: -9999px;
                        opacity: 0;
                        height: 0;
                        width: 0;
                        overflow: hidden;
                        pointer-events: none;
                        tab-index: -1;
                    }

                    /* +++ MOBILE RESPONSIVENESS (ONLY affects screens smaller than 600px) +++ */
                    @media (max-width: 600px) {
                        .interest-card {
                            /* On mobile, we USE border-box to ensure padding fits inside the screen width */
                            box-sizing: border-box; 
                            padding: 25px 20px;
                            margin-top: 0;
                            box-shadow: 4px 4px 0px ${BRAND.teal};
                            border-radius: 16px;
                        }

                        h1 {
                            font-size: 1.75rem !important;
                        }

                        .form-input {
                            font-size: 0.95rem;
                            padding: 10px 14px;
                        }

                        .btn-submit {
                            font-size: 1rem;
                            padding: 12px;
                        }
                    }
                `}
            </style>

            <div className="pattern-bg"></div>

            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div className="interest-card">
                    {!submitted ? (
                        <>
                            <Link to="/" className="btn-back">← Back to Home</Link>
                            <h1 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: '900', color: BRAND.charcoal, margin: '0 0 10px 0', fontSize: '2rem' }}>
                                Book a Demo
                            </h1>
                            <p style={{ color: BRAND.textGrey, marginBottom: '30px', lineHeight: '1.5' }}>
                                Interested in ClassLabs? We are offering a limited number of free pilots for schools. Leave your details and we'll be in touch to arrange a personalised demo!
                            </p>

                            <form onSubmit={handleSubmit}>
                                <div>
                                    <label className="form-label">Full Name</label>
                                    <input 
                                        required 
                                        name="name" 
                                        type="text" 
                                        className="form-input" 
                                        placeholder="e.g. Sarah Smith"
                                        value={formData.name}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div>
                                    <label className="form-label">School Email</label>
                                    <input 
                                        required 
                                        name="email" 
                                        type="email" 
                                        className="form-input" 
                                        placeholder="sarah@school.edu" 
                                        value={formData.email}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div>
                                    <label className="form-label">Phone Number (Optional)</label>
                                    <input
                                        name="phone"
                                        type="tel"
                                        className="form-input"
                                        placeholder="e.g. 07700 900077"
                                        value={formData.phone}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div>
                                    <label className="form-label">School Name</label>
                                    <input 
                                        required 
                                        name="school" 
                                        type="text" 
                                        className="form-input" 
                                        placeholder="e.g. St Mary's Academy"
                                        value={formData.school}
                                        onChange={handleChange}
                                    />
                                </div>

                                <div>
                                    <label className="form-label">Role</label>
                                    <select 
                                        name="role" 
                                        className="form-input"
                                        value={formData.role}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="" disabled>Select your role...</option>
                                        <option value="SENCO">SENCO</option>
                                        <option value="Teacher">Teacher</option>
                                        <option value="Senior Leader">Senior Leader</option>
                                        <option value="Teaching Assistant">Teaching Assistant</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="form-label">How did you hear about us?</label>
                                    <select
                                        name="heardAboutUs"
                                        className="form-input"
                                        value={formData.heardAboutUs}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="" disabled>Please select...</option>
                                        <option value="Colleague/Word of Mouth">Colleague / Word of Mouth</option>
                                        <option value="LinkedIn">LinkedIn</option>
                                        <option value="Search Engine">Search Engine</option>
                                        <option value="Conference/Event">Conference / Event</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>

                                <div className="hp-field" aria-hidden="true">
                                    <label htmlFor="website">Website</label>
                                    <input
                                        id="website"
                                        name="website"
                                        type="text"
                                        tabIndex={-1}
                                        autoComplete="off"
                                        value={formData.website}
                                        onChange={handleChange}
                                    />
                                </div>

                                <button type="submit" className="btn-submit" disabled={loading}>
                                    {loading ? 'Submitting...' : 'Request Demo'}
                                </button>
                            </form>
                        </>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '20px 0' }}>
                            <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🎉</div>
                            <h2 style={{ fontFamily: 'Nunito', fontWeight: '900', color: BRAND.charcoal, margin: '0 0 15px 0' }}>
                                Thank You!
                            </h2>
                            <p style={{ color: BRAND.textGrey, fontSize: '1.1rem', lineHeight: '1.6' }}>
                                We've received your interest. Our team will be in touch with you shortly to schedule your demo.
                            </p>
                            <Link to="/" className="btn-submit" style={{ display: 'inline-block', textDecoration: 'none', marginTop: '30px', width: 'auto', padding: '12px 30px' }}>
                                Return Home
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RegisterInterest;